import React, { useState, useEffect } from "react";
import VideoModal from "../components/VideoModal";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";
import paramrutData from "../data/paramrut3.json";

const PARAMRUT_INDEX_URL = "/data/paramrut-index.json";

const ITEMS = [...paramrutData].sort(
  (a, b) => new Date(b.prvdate) - new Date(a.prvdate),
);

const YEARS = [
  "All",
  ...Array.from(new Set(ITEMS.map((i) => i.prvyear))).sort((a, b) => b - a),
];

const gujaratiCollator = new Intl.Collator("gu-IN");

function getVideoId(url) {
  if (!url) return null;
  const m = url.match(/[?&]v=([^&]+)/);
  return m ? m[1] : null;
}

function getVishay(prvinfo) {
  if (!prvinfo) return "";
  const start = prvinfo.indexOf("વિષય");
  if (start === -1) return "";
  // Stop at any sub-section label that follows the topics
  const stops = ["પ્રસંગ", "સ્વામીની વાત", "વચનામૃત", "એનાલોજી"]
    .map((s) => prvinfo.indexOf(s, start))
    .filter((i) => i !== -1);
  const end = stops.length > 0 ? Math.min(...stops) : prvinfo.length;
  return prvinfo.slice(start, end).trim().replace(/,\s*$/, "");
}

// Extract text for a labelled section up to the next section label
function getSection(prvinfo, label) {
  if (!prvinfo) return "";
  const start = prvinfo.indexOf(label);
  if (start === -1) return "";
  const allLabels = ["પ્રસંગ", "સ્વામીની વાત", "વચનામૃત", "વિષય", "એનાલોજી"];
  const stops = allLabels
    .filter((l) => l !== label)
    .map((l) => prvinfo.indexOf(l, start + label.length))
    .filter((i) => i !== -1);
  const end = stops.length > 0 ? Math.min(...stops) : prvinfo.length;
  return prvinfo.slice(start, end).trim().replace(/,\s*$/, "");
}

function collectTopics(labelPrefix) {
  const set = new Set();
  ITEMS.forEach((item) => {
    const section = getSection(item.prvinfo, labelPrefix);
    if (!section) return;
    const body = section.replace(new RegExp(`^${labelPrefix}\\s*:\\s*`), "");
    body.split(/,/).forEach((t) => {
      const trimmed = t.trim();
      if (trimmed) set.add(trimmed);
    });
  });
  return Array.from(set).sort();
}

// Collect all unique vishay topics (split by comma, strip label)
const ALL_VISHAY_TOPICS = (() => {
  const set = new Set();
  ITEMS.forEach((item) => {
    const vishay = getVishay(item.prvinfo);
    if (!vishay) return;
    // Remove leading "વિષય:" or "વિષય :"
    const body = vishay.replace(/^વિષય\s*:\s*/, "");
    body.split(/,/).forEach((t) => {
      const trimmed = t.trim();
      if (trimmed) set.add(trimmed);
    });
  });
  return Array.from(set).sort(gujaratiCollator.compare);
})();

const ALL_VAATO_TOPICS = collectTopics("સ્વામીની વાત");
const ALL_VACHAN_TOPICS = collectTopics("વચનામૃત");
const ALL_ANALOGY_TOPICS = collectTopics("એનાલોજી");
// Split prasang body by Gujarati serial numbers: ૧) ૨) or (૧) (૨)
const ALL_PRASANG_TOPICS = (() => {
  const set = new Set();
  const serialRe = /(?:\([\u0AE6-\u0AEF]+\)|[\u0AE6-\u0AEF]+\))/;
  ITEMS.forEach((item) => {
    const section = getSection(item.prvinfo, "પ્રસંગ");
    if (!section) return;
    const body = section.replace(/^પ્રસંગ\s*:\s*/, "");
    body.split(serialRe).forEach((t) => {
      const trimmed = t.trim().replace(/^[,،]\s*/, "");
      if (trimmed) set.add(trimmed);
    });
  });
  return Array.from(set).sort(gujaratiCollator.compare);
})();

export default function AksharVaniSearch() {
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("All");
  const [contentSearch, setContentSearch] = useState("");
  const [contentIndex, setContentIndex] = useState([]);
  const [contentSearchResults, setContentSearchResults] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState(null);
  const [modalVideoTitle, setModalVideoTitle] = useState(null);
  const [selectedVishay, setSelectedVishay] = useState("");
  const [vishayOpen, setVishayOpen] = useState(false);
  const [selectedRef, setSelectedRef] = useState(""); // vaato/vachan chip
  const [refPanelOpen, setRefPanelOpen] = useState(false);
  const [selectedPrasang, setSelectedPrasang] = useState("");
  const [prasangPanelOpen, setPrasangPanelOpen] = useState(false);

  useEffect(() => {
    fetch(PARAMRUT_INDEX_URL)
      .then((r) => r.json())
      .then((data) => setContentIndex(data))
      .catch(() => {});
  }, []);

  const handleContentSearch = () => {
    const word = contentSearch.trim();
    if (word.length < 2) return;
    const matchFiles = new Set(
      contentIndex
        .filter((e) => e.text.toLowerCase().includes(word.toLowerCase()))
        .map((e) => e.file),
    );
    setContentSearchResults(matchFiles);
  };

  const clearContentSearch = () => {
    setContentSearch("");
    setContentSearchResults(null);
  };

  const filtered = ITEMS.filter((item) => {
    if (selectedYear !== "All" && item.prvyear !== selectedYear) return false;
    if (
      contentSearchResults !== null &&
      !contentSearchResults.has(item.prvfile)
    )
      return false;
    if (selectedVishay) {
      const vishay = getVishay(item.prvinfo);
      if (!vishay.includes(selectedVishay)) return false;
    }
    if (selectedRef) {
      if (!item.prvinfo?.includes(selectedRef)) return false;
    }
    if (selectedPrasang) {
      if (!item.prvinfo?.includes(selectedPrasang)) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        item.prvtitle?.toLowerCase().includes(q) ||
        item.prvinfo?.toLowerCase().includes(q) ||
        item.prvplace?.toLowerCase().includes(q) ||
        item.prvdate?.includes(q)
      );
    }
    return true;
  });

  const openVideo = (item) => {
    const vid = getVideoId(item.prvvideourl);
    if (!vid) return;
    setModalVideoId(vid);
    setModalVideoTitle(item.prvtitle);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalVideoId(null);
    setModalVideoTitle(null);
  };

  return (
    <main className="page-content pravachan-page akshar-search-page">
      <section className="page-header">
        <h1 className="section-title">
          Akshar Vani Search
          <span className="akshar-result-count" style={{ fontSize: "1rem", marginLeft: "12px", color: "#888" }}>
            ({filtered.length})
          </span>
        </h1>
        <p className="header-subtitle">
          Guruhari P.P. Prabodhjivan Swamiji Pravachan
        </p>
      </section>

      <section className="page-body">
        {/* Row 1: Title / place / info filter */}
        <div className="word-search-bar" style={{ marginBottom: "10px" }}>
          <input
            className="word-search-input"
            type="text"
            placeholder="Search title, place, info..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="word-search-clear" onClick={() => setSearch("")}>
              Clear
            </button>
          )}
          <select
            aria-label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "1.5px solid rgba(102,126,234,0.4)",
              fontSize: "0.95em",
              color: "#3a3a5e",
              background: "rgba(255,255,255,0.9)",
              cursor: "pointer",
            }}
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y === "All" ? "All Years" : y}
              </option>
            ))}
          </select>
        </div>

        {/* Row 2: HTML content word search */}
        <div className="word-search-bar" style={{ marginBottom: "10px" }}>
          <input
            className="word-search-input"
            type="text"
            placeholder="Search in pravachan text..."
            value={contentSearch}
            onChange={(e) => setContentSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleContentSearch()}
          />
          <button
            className="word-search-btn"
            onClick={handleContentSearch}
            disabled={contentSearch.trim().length < 2}
          >
            Search Text
          </button>
          {contentSearchResults !== null && (
            <button className="word-search-clear" onClick={clearContentSearch}>
              Clear ({contentSearchResults.size})
            </button>
          )}
        </div>

        {/* Row 3: Vishay topic chips (collapsible) */}
        {ALL_VISHAY_TOPICS.length > 0 && (
          <div
            className="akshar-filter-panel"
            style={{
              marginBottom: "20px",
              borderRadius: "12px",
              border: "1px solid rgba(102,126,234,0.15)",
              background: "rgba(102,126,234,0.05)",
              overflow: "hidden",
            }}
          >
            {/* Toggle header */}
            <button
              className="akshar-filter-toggle"
              aria-expanded={vishayOpen}
              aria-controls="akshar-topics"
              onClick={() => setVishayOpen((o) => !o)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 14px",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  fontSize: "0.82em",
                  color: "#667eea",
                  fontWeight: 600,
                }}
              >
                વિષય
                {selectedVishay && (
                  <span style={{ marginLeft: "6px", color: "#764ba2" }}>
                    — {selectedVishay}
                  </span>
                )}
              </span>
              <span style={{ fontSize: "0.78em", color: "#667eea" }}>
                {vishayOpen ? "▲" : "▼"}
              </span>
            </button>

            {/* Chips panel */}
            {vishayOpen && (
              <div
                id="akshar-topics"
                role="group"
                aria-label="Topics"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  padding: "6px 14px 12px",
                }}
              >
                {ALL_VISHAY_TOPICS.map((topic) => (
                  <button
                    key={topic}
                    className="akshar-filter-chip"
                    aria-pressed={selectedVishay === topic}
                    onClick={() =>
                      setSelectedVishay(selectedVishay === topic ? "" : topic)
                    }
                    style={{
                      padding: "3px 10px",
                      borderRadius: "20px",
                      border: "1.5px solid",
                      borderColor:
                        selectedVishay === topic
                          ? "#667eea"
                          : "rgba(102,126,234,0.35)",
                      background:
                        selectedVishay === topic
                          ? "linear-gradient(135deg,#667eea,#764ba2)"
                          : "rgba(255,255,255,0.8)",
                      color: selectedVishay === topic ? "#fff" : "#3a3a5e",
                      fontSize: "0.78em",
                      cursor: "pointer",
                      fontWeight: selectedVishay === topic ? 600 : 400,
                      transition: "all 0.15s",
                    }}
                  >
                    {topic}
                  </button>
                ))}
                {selectedVishay && (
                  <button
                    className="akshar-filter-clear"
                    onClick={() => setSelectedVishay("")}
                    style={{
                      padding: "3px 10px",
                      borderRadius: "20px",
                      border: "1.5px solid #e05",
                      background: "rgba(238,0,85,0.08)",
                      color: "#e05",
                      fontSize: "0.78em",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Row 4: સ્વામીની વાત + વચનામૃત combined panel (collapsible) */}
        {(ALL_VAATO_TOPICS.length > 0 ||
          ALL_VACHAN_TOPICS.length > 0 ||
          ALL_ANALOGY_TOPICS.length > 0) && (
          <div
            className="akshar-filter-panel akshar-filter-panel--secondary"
            style={{
              marginBottom: "20px",
              borderRadius: "12px",
              border: "1px solid rgba(118,75,162,0.18)",
              background: "rgba(118,75,162,0.05)",
              overflow: "hidden",
            }}
          >
            <button
              className="akshar-filter-toggle"
              aria-expanded={refPanelOpen}
              aria-controls="akshar-references"
              onClick={() => setRefPanelOpen((o) => !o)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 14px",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  fontSize: "0.82em",
                  color: "#764ba2",
                  fontWeight: 600,
                }}
              >
                સ્વામીની વાત / વચનામૃત / એનાલોજી
                {selectedRef && (
                  <span style={{ marginLeft: "6px", color: "#667eea" }}>
                    — {selectedRef}
                  </span>
                )}
              </span>
              <span style={{ fontSize: "0.78em", color: "#764ba2" }}>
                {refPanelOpen ? "▲" : "▼"}
              </span>
            </button>

            {refPanelOpen && (
              <div id="akshar-references" role="group" aria-label="References" style={{ padding: "6px 14px 12px" }}>
                {ALL_VAATO_TOPICS.length > 0 && (
                  <>
                    <div
                      className="akshar-filter-heading"
                      style={{
                        fontSize: "0.74em",
                        color: "#764ba2",
                        fontWeight: 600,
                        marginBottom: "5px",
                      }}
                    >
                      સ્વામીની વાત:
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginBottom: "10px",
                      }}
                    >
                      {ALL_VAATO_TOPICS.map((topic) => (
                        <button
                          key={topic}
                          className="akshar-filter-chip"
                          aria-pressed={selectedRef === topic}
                          onClick={() =>
                            setSelectedRef(selectedRef === topic ? "" : topic)
                          }
                          style={{
                            padding: "3px 10px",
                            borderRadius: "20px",
                            border: "1.5px solid",
                            borderColor:
                              selectedRef === topic
                                ? "#764ba2"
                                : "rgba(118,75,162,0.35)",
                            background:
                              selectedRef === topic
                                ? "linear-gradient(135deg,#764ba2,#667eea)"
                                : "rgba(255,255,255,0.8)",
                            color: selectedRef === topic ? "#fff" : "#3a3a5e",
                            fontSize: "0.78em",
                            cursor: "pointer",
                            fontWeight: selectedRef === topic ? 600 : 400,
                            transition: "all 0.15s",
                          }}
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {ALL_VACHAN_TOPICS.length > 0 && (
                  <>
                    <div
                      className="akshar-filter-heading"
                      style={{
                        fontSize: "0.74em",
                        color: "#764ba2",
                        fontWeight: 600,
                        marginBottom: "5px",
                      }}
                    >
                      વચનામૃત:
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginBottom: "6px",
                      }}
                    >
                      {ALL_VACHAN_TOPICS.map((topic) => (
                        <button
                          key={topic}
                          className="akshar-filter-chip"
                          aria-pressed={selectedRef === topic}
                          onClick={() =>
                            setSelectedRef(selectedRef === topic ? "" : topic)
                          }
                          style={{
                            padding: "3px 10px",
                            borderRadius: "20px",
                            border: "1.5px solid",
                            borderColor:
                              selectedRef === topic
                                ? "#764ba2"
                                : "rgba(118,75,162,0.35)",
                            background:
                              selectedRef === topic
                                ? "linear-gradient(135deg,#764ba2,#667eea)"
                                : "rgba(255,255,255,0.8)",
                            color: selectedRef === topic ? "#fff" : "#3a3a5e",
                            fontSize: "0.78em",
                            cursor: "pointer",
                            fontWeight: selectedRef === topic ? 600 : 400,
                            transition: "all 0.15s",
                          }}
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {ALL_ANALOGY_TOPICS.length > 0 && (
                  <>
                    <div
                      className="akshar-filter-heading"
                      style={{
                        fontSize: "0.74em",
                        color: "#764ba2",
                        fontWeight: 600,
                        marginBottom: "5px",
                      }}
                    >
                      એનાલોજી:
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginBottom: "6px",
                      }}
                    >
                      {ALL_ANALOGY_TOPICS.map((topic) => (
                        <button
                          key={topic}
                          className="akshar-filter-chip"
                          aria-pressed={selectedRef === topic}
                          onClick={() =>
                            setSelectedRef(selectedRef === topic ? "" : topic)
                          }
                          style={{
                            padding: "3px 10px",
                            borderRadius: "20px",
                            border: "1.5px solid",
                            borderColor:
                              selectedRef === topic
                                ? "#764ba2"
                                : "rgba(118,75,162,0.35)",
                            background:
                              selectedRef === topic
                                ? "linear-gradient(135deg,#764ba2,#667eea)"
                                : "rgba(255,255,255,0.8)",
                            color: selectedRef === topic ? "#fff" : "#3a3a5e",
                            fontSize: "0.78em",
                            cursor: "pointer",
                            fontWeight: selectedRef === topic ? 600 : 400,
                            transition: "all 0.15s",
                          }}
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {selectedRef && (
                  <button
                    className="akshar-filter-clear"
                    onClick={() => setSelectedRef("")}
                    style={{
                      marginTop: "4px",
                      padding: "3px 10px",
                      borderRadius: "20px",
                      border: "1.5px solid #e05",
                      background: "rgba(238,0,85,0.08)",
                      color: "#e05",
                      fontSize: "0.78em",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Row 5: પ્રસંગ panel (collapsible) */}
        {ALL_PRASANG_TOPICS.length > 0 && (
          <div
            className="akshar-filter-panel akshar-filter-panel--secondary"
            style={{
              marginBottom: "20px",
              borderRadius: "12px",
              border: "1px solid rgba(46,139,87,0.18)",
              background: "rgba(46,139,87,0.04)",
              overflow: "hidden",
            }}
          >
            <button
              className="akshar-filter-toggle"
              aria-expanded={prasangPanelOpen}
              aria-controls="akshar-prasangs"
              onClick={() => setPrasangPanelOpen((o) => !o)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 14px",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  fontSize: "0.82em",
                  color: "#2e8b57",
                  fontWeight: 600,
                }}
              >
                પ્રસંગ
                {selectedPrasang && (
                  <span style={{ marginLeft: "6px", color: "#667eea" }}>
                    — {selectedPrasang}
                  </span>
                )}
              </span>
              <span style={{ fontSize: "0.78em", color: "#2e8b57" }}>
                {prasangPanelOpen ? "▲" : "▼"}
              </span>
            </button>
            {prasangPanelOpen && (
              <div
                id="akshar-prasangs"
                role="group"
                aria-label="Prasangs"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  padding: "6px 14px 12px",
                }}
              >
                {ALL_PRASANG_TOPICS.map((topic) => (
                  <button
                    key={topic}
                    className="akshar-filter-chip"
                    aria-pressed={selectedPrasang === topic}
                    onClick={() =>
                      setSelectedPrasang(selectedPrasang === topic ? "" : topic)
                    }
                    style={{
                      padding: "3px 10px",
                      borderRadius: "20px",
                      border: "1.5px solid",
                      borderColor:
                        selectedPrasang === topic
                          ? "#2e8b57"
                          : "rgba(46,139,87,0.35)",
                      background:
                        selectedPrasang === topic
                          ? "linear-gradient(135deg,#2e8b57,#3cb371)"
                          : "rgba(255,255,255,0.8)",
                      color: selectedPrasang === topic ? "#fff" : "#1a3a2a",
                      fontSize: "0.78em",
                      cursor: "pointer",
                      fontWeight: selectedPrasang === topic ? 600 : 400,
                      transition: "all 0.15s",
                    }}
                  >
                    {topic}
                  </button>
                ))}
                {selectedPrasang && (
                  <button
                    className="akshar-filter-clear"
                    onClick={() => setSelectedPrasang("")}
                    style={{
                      padding: "3px 10px",
                      borderRadius: "20px",
                      border: "1.5px solid #e05",
                      background: "rgba(238,0,85,0.08)",
                      color: "#e05",
                      fontSize: "0.78em",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Results grid */}
        {filtered.length === 0 && (
          <p
            className="akshar-empty"
            style={{ textAlign: "center", color: "#9a9abf", padding: "40px 0" }}
          >
            No results found.
          </p>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "16px",
          }}
        >
          {filtered.map((item) => {
            const videoId = getVideoId(item.prvvideourl);
            return (
              <div
                key={item.prvid}
                className="akshar-result-card"
                style={{
                  borderRadius: "14px",
                  border: "1px solid rgba(102,126,234,0.15)",
                  background: "rgba(255,255,255,0.9)",
                  boxShadow: "0 4px 16px rgba(102,126,234,0.08)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  transition: "box-shadow 0.15s",
                }}
              >
                {/* YouTube thumbnail */}
                {videoId ? (
                  <div
                    onClick={() => openVideo(item)}
                    style={{ position: "relative", cursor: "pointer" }}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                      alt={item.prvtitle}
                      data-thumbnail
                      style={{
                        width: "100%",
                        display: "block",
                        aspectRatio: "16/9",
                        objectFit: "cover",
                      }}
                    />
                    {/* Play overlay */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0,0,0,0.18)",
                        transition: "background 0.15s",
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          background: "rgba(255,0,0,0.85)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "1.1em",
                          color: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        }}
                      >
                        ▶
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="akshar-video-placeholder"
                    style={{
                      width: "100%",
                      aspectRatio: "16/9",
                      background:
                        "linear-gradient(135deg,rgba(102,126,234,0.12),rgba(118,75,162,0.1))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#aaa",
                      fontSize: "0.85em",
                    }}
                  >
                    No Video
                  </div>
                )}

                {/* Card body */}
                <div
                  style={{
                    padding: "12px 14px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <div
                    className="akshar-card-title"
                    style={{
                      fontWeight: 700,
                      fontSize: "0.88em",
                      color: "#3a3a5e",
                      lineHeight: 1.35,
                    }}
                  >
                    {item.prvtitle}
                  </div>
                  <div
                    className="akshar-card-meta"
                    style={{
                      fontSize: "0.76em",
                      color: "#888",
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{item.prvdate}</span>
                    {item.prvplace && <span>📍 {item.prvplace}</span>}
                  </div>
                  {item.prvinfo && (
                    <div
                      className="akshar-card-info"
                      style={{
                        fontSize: "0.75em",
                        color: "#555",
                        lineHeight: 1.45,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.prvinfo}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <VideoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        videoId={modalVideoId}
        title={modalVideoTitle}
      />
    </main>
  );
}
